import React, { useEffect, useMemo, useRef, useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import type { IPagination } from "@common-shared/types/general.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { NirvanaBreathingLoaderElement } from "@front-shared/elements/nirvanaBreathingLoader.element.tsx"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper.ts"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper.tsx"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types.ts"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { NETWORK_STATUS_CONFIG } from "../../../config/networkStatus.config.ts"
import { POLICIES_CONFIG } from "../../../config/policies.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { useScrollableElementBoundaries } from "../../../hooks/scrollableElementBoundaries.hook.ts"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { networkStatusCreateRulesFromReview } from "../../../store/thunks/networkStatus.thunks"
import type { IReviewNewRulesLocationState } from "../../../types/networkStatus.types"
import { PoliciesListPaginationComponent } from "../../policies/policiesList/policiesListPagination.component"
import { NetworkStatusRulesBulkUploadPopupComponent } from "../../popups/networkStatusRulesBulkUploadPopup.component"
import { RulesTableComponent } from "../rulesList/rulesTable.component"
import { ReviewNewRulesFloatingControlComponent } from "./reviewNewRulesFloatingControl.component"

import closeDarkImg from "@front-shared/assets/images/icons/close-dark.svg"

export const ReviewNewRulesComponent: React.FunctionComponent<
    IReviewNewRulesComponentProps
> = props => {
    useScrollFix()

    useHasPermissions(
        OneVerifyPermission.manageNetworkStatusRules,
        undefined,
        true,
    )

    useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_NETWORK_STATUS_RULES,
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const dispatch = useAppDispatch()

    const scrollableTableRef = useRef<HTMLDivElement>(null)

    const scrollableTableBoundaries =
        useScrollableElementBoundaries(scrollableTableRef)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const isSubmitting = useMemo<boolean>(() => {
        return RuntimeHelper.isNetworkStatusCreateRulesFromReviewLoading()
    }, [runtimeState.isLoading])

    const [isReuploadPopupActive, setIsReuploadPopupActive] =
        useState<boolean>(false)

    const [reviewStart, setReviewStart] = useState(0)

    const [selectedPaginationOption, setSelectedPaginationOption] =
        useState<ISelectRenderedOption>(
            BrowserStorageHelper.get<ISelectRenderedOption>(
                NETWORK_STATUS_CONFIG.reviewListPerPageStorageKey,
                undefined,
                BrowserStorageType.sessionStorage,
            ) ?? POLICIES_CONFIG.availablePaginationOptions[0],
        )

    const reviewCount = Number(selectedPaginationOption.value)
    const reviewTotal = props.state.rules.length

    // Start of the last page, not the last item offset: clamping to
    // total - count lands between pages and desyncs the page indicator
    const maxReviewStart = useMemo<number>(() => {
        return (
            Math.max(0, Math.ceil(reviewTotal / reviewCount) - 1) * reviewCount
        )
    }, [reviewTotal, reviewCount])

    const reviewClampedStart = useMemo<number>(() => {
        return Math.min(reviewStart, maxReviewStart)
    }, [reviewStart, maxReviewStart])

    // Rules that have no error (duplicates are marked with errors by backend validation)
    const validRulesToSend = useMemo(() => {
        if (!props.state.rules) {
            return []
        }

        const errorIndices = new Set(props.state.errors.map(e => e.index))

        return props.state.rules.filter((_, i) => !errorIndices.has(i))
    }, [props.state.rules, props.state.errors])

    const pageRules = useMemo(
        () =>
            props.state.rules.slice(
                reviewClampedStart,
                reviewClampedStart + reviewCount,
            ),
        [props.state.rules, reviewClampedStart, reviewCount],
    )

    useEffect(() => {
        BrowserStorageHelper.set(
            NETWORK_STATUS_CONFIG.reviewListPerPageStorageKey,
            selectedPaginationOption,
            BrowserStorageType.sessionStorage,
        )
    }, [selectedPaginationOption])

    // Reset to first page when user changes page size (PoliciesListPaginationComponent only calls setSelectedPaginationOption + onPaginationChange)
    useEffect(() => {
        setReviewStart(0)
    }, [selectedPaginationOption])

    useEffect(() => {
        if (reviewStart > maxReviewStart) {
            setReviewStart(maxReviewStart)
        }
    }, [maxReviewStart, reviewStart])

    const reviewNextPagination = useMemo<IPagination>(() => {
        const start = reviewClampedStart + reviewCount

        return {
            start,
            count: reviewCount,
            total: reviewTotal,
            moreAvailable: start < reviewTotal,
        }
    }, [reviewClampedStart, reviewCount, reviewTotal])

    const onReviewGetNewItems = (pagination: IPagination) => {
        setReviewStart(pagination.start)
    }

    const onReuploadCsv = () => {
        setIsReuploadPopupActive(true)
    }

    const onUploadWithErrorsFromPopup = (
        data: IReviewNewRulesLocationState,
    ) => {
        setIsReuploadPopupActive(false)
        setReviewStart(0)

        props.onUploadWithErrors(data)
    }

    const onAddValidRules = () => {
        if (!selectedPracticeRole || !validRulesToSend.length) {
            return
        }

        ConfirmationHelper.enableConfirmation(
            () => {
                dispatch(
                    networkStatusCreateRulesFromReview({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            rules: validRulesToSend,
                        },
                        onSuccess: props.onAddValidRulesSuccess,
                    }),
                )
            },
            `Are you sure you want to create a new version?`,
            `Creating a new version with the valid rules will make it the active version for the practice.`,
        )
    }

    return (
        <div className="pb-32 relative">
            <NirvanaBreathingLoaderElement isActive={isSubmitting} />

            <PageHelmetElement
                title="Review New Rules"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <div
                className="relative px-[24px] py-[12px] mb-[24px]"
                style={{
                    right: scrollableTableBoundaries?.x,
                }}
            >
                <div className="flex items-center md:block">
                    <PrimaryText
                        className="mr-[16px]"
                        size={29}
                        lineHeight={35}
                        weight="semibold"
                    >
                        Review New Rules
                    </PrimaryText>

                    <span
                        className="
                            flex items-center px-[8px] py-[2px]
                            rounded-[4px] bg-white
                        "
                    >
                        <PrimaryText typography="h6" className="mr-[4px]">
                            {props.state.filename}
                        </PrimaryText>

                        <div
                            onClick={props.onClose}
                            className="p-[4px] cursor-pointer"
                        >
                            <img src={closeDarkImg} alt="Close" />
                        </div>
                    </span>
                </div>
            </div>

            <div className="relative pb-[120px]">
                <RulesTableComponent
                    scrollableElementRef={scrollableTableRef}
                    rules={pageRules}
                    errors={props.state.errors}
                    showIdColumn={false}
                />

                <PoliciesListPaginationComponent
                    className="md:px-[24px] px-[48px]"
                    style={{
                        right: scrollableTableBoundaries?.x,
                    }}
                    getNewItems={onReviewGetNewItems}
                    nextPagination={reviewNextPagination}
                    onPaginationChange={undefined}
                    selectedPaginationOption={selectedPaginationOption}
                    setSelectedPaginationOption={setSelectedPaginationOption}
                />
            </div>

            <ReviewNewRulesFloatingControlComponent
                invalidCount={props.state.errors.length}
                totalCount={
                    props.state.totalRowCount ?? props.state.rules.length
                }
                validCount={validRulesToSend.length}
                onReuploadCsv={onReuploadCsv}
                onAddValidRules={onAddValidRules}
                isSubmitting={isSubmitting}
            />

            <NetworkStatusRulesBulkUploadPopupComponent
                isActive={isReuploadPopupActive}
                onDone={() => {
                    setIsReuploadPopupActive(false)
                }}
                onValidationDone={onUploadWithErrorsFromPopup}
            />
        </div>
    )
}

export interface IReviewNewRulesComponentProps {
    state: IReviewNewRulesLocationState
    onClose: () => void
    onUploadWithErrors: (data: IReviewNewRulesLocationState) => void
    onAddValidRulesSuccess: () => void
}
